import React, { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { 
  FileText, 
  Calendar as CalendarIcon, 
  AlertTriangle, 
  Search,
  Zap,
  Building2,
  Clock,
  Euro,
  TrendingUp,
  Eye,
  RefreshCw,
  MapPin,
  User,
  Trash2
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContractsPage = () => {
  const [contracts, setContracts] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedContract, setSelectedContract] = useState(null);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  const [showDecommissionsDialog, setShowDecommissionsDialog] = useState(false);
  const [decommissions, setDecommissions] = useState([]);
  const [loadingDecommissions, setLoadingDecommissions] = useState(false);
  const [decommissionForm, setDecommissionForm] = useState({
    type: 'partielle',
    montant: '',
    date_decommission: new Date().toISOString().split('T')[0],
    raison: ''
  });
  const [showContractFilters, setShowContractFilters] = useState(false);
  
  // Filtres avancés
  const [filterAssignedTo, setFilterAssignedTo] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterStartDate, setFilterStartDate] = useState(null);
  const [filterEndDate, setFilterEndDate] = useState(null);
  const [commercials, setCommercials] = useState([]);
  const [showTreasuryModal, setShowTreasuryModal] = useState(false);
  const [contractTreasuryData, setContractTreasuryData] = useState({
    montant_avance: 0,
    paiement_mensuel: 0,
    avances_supplementaires: []
  });

  useEffect(() => {
    Promise.all([fetchContracts(), fetchClients(), fetchCommercials()]);
  }, []);

  // Helper pour calculer le total réel d'un contrat (base + avances + mensuels)
  const calculateTotalContractValue = (contract) => {
    if (!contract) return 0;
    
    let total = contract.annual_cost || 0;
    
    // Ajouter avance initiale
    if (contract.montant_avance) {
      total += contract.montant_avance;
    }
    
    // Ajouter deuxième tranche
    if (contract.montant_deuxieme_tranche) {
      total += contract.montant_deuxieme_tranche;
    }
    
    // Ajouter avances supplémentaires
    if (contract.avances_supplementaires && Array.isArray(contract.avances_supplementaires)) {
      const totalAvancesSup = contract.avances_supplementaires.reduce((sum, avance) => {
        return sum + (parseFloat(avance.montant) || 0);
      }, 0);
      total += totalAvancesSup;
    }
    
    // Ajouter total paiements mensuels
    if (contract.paiement_mensuel && contract.duree_mois) {
      total += contract.paiement_mensuel * contract.duree_mois;
    }
    
    return total;
  };

  // Helper pour calculer uniquement le total des avances
  const calculateTotalAvances = (contract) => {
    if (!contract) return 0;
    
    let total = 0;
    
    if (contract.montant_avance) {
      total += contract.montant_avance;
    }
    
    if (contract.montant_deuxieme_tranche) {
      total += contract.montant_deuxieme_tranche;
    }
    
    if (contract.avances_supplementaires && Array.isArray(contract.avances_supplementaires)) {
      total += contract.avances_supplementaires.reduce((sum, avance) => {
        return sum + (parseFloat(avance.montant) || 0);
      }, 0);
    }
    
    return total;
  };


  const fetchContracts = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/contracts/details`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log("Contracts with creator info loaded:", response.data); // Debug
      setContracts(response.data);
    } catch (error) {
      console.error("Error fetching contracts:", error);
      toast.error("Erreur lors du chargement des contrats");
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/clients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClients(response.data);
      console.log('Clients loaded:', response.data.length); // Debug
    } catch (error) {
      console.error("Error fetching clients:", error);
    }
  };

  const fetchCommercials = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Filtrer seulement les commerciaux et régies
      const commercialUsers = response.data.filter(u => 
        u.role === 'commercial' || u.role === 'regie'
      );
      setCommercials(commercialUsers);
    } catch (error) {
      console.error("Error fetching commercials:", error);
    }
  };

  const getClientName = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    if (!client) return "Client inconnu";
    
    // Priorité : company_name, sinon prénom + nom, sinon email
    if (client.company_name && client.company_name.trim()) {
      return client.company_name;
    } else if (client.prenom || client.nom) {
      return `${client.prenom || ''} ${client.nom || ''}`.trim();
    } else if (client.email) {
      return client.email;
    }
    return "Client inconnu";
  };

  const getStatusColor = (status) => {
    const colors = {
      active: "bg-emerald-100 text-emerald-800",
      expired: "bg-red-100 text-red-800",
      pending: "bg-amber-100 text-amber-800",
      cancelled: "bg-gray-100 text-gray-800"
    };
    return colors[status] || colors.active;
  };

  const getStatusLabel = (status) => {
    const labels = {
      active: "Actif",
      expired: "Expiré",
      pending: "En attente",
      cancelled: "Annulé"
    };
    return labels[status] || status;
  };

  const getDaysUntilExpiry = (endDate) => {
    const now = new Date();
    const expiry = new Date(endDate);
    const diffTime = expiry - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getExpiryBadge = (endDate) => {
    const daysUntil = getDaysUntilExpiry(endDate);
    
    if (daysUntil < 0) {
      return <Badge className="bg-red-100 text-red-800">Expiré</Badge>;
    } else if (daysUntil <= 7) {
      return <Badge className="bg-red-100 text-red-800">⚠️ {daysUntil}j</Badge>;
    } else if (daysUntil <= 15) {
      return <Badge className="bg-amber-100 text-amber-800">⏰ {daysUntil}j</Badge>;
    } else if (daysUntil <= 30) {
      return <Badge className="bg-blue-100 text-blue-800">📅 {daysUntil}j</Badge>;
    }
    return null;
  };

  const handleEditContract = (contract) => {
    setEditFormData({
      ...contract,
      start_date: contract.start_date.split('T')[0],
      end_date: contract.end_date.split('T')[0]
    });
    setSelectedContract(contract);
    setShowDetailsDialog(false);
    setShowEditDialog(true);
  };

  const handleSaveContract = async () => {
    try {
      await axios.put(`${API}/contracts/${selectedContract.id}`, editFormData);
      toast.success("Contrat mis à jour avec succès");
      setShowEditDialog(false);
      fetchContracts();
    } catch (error) {
      console.error("Error updating contract:", error);
      toast.error("Erreur lors de la mise à jour du contrat");
    }
  };

  const handleDeleteContract = async (contractId) => {
    if (!confirm("Êtes-vous sûr de vouloir supprimer ce contrat ?")) {
      return;
    }

    try {
      await axios.delete(`${API}/contracts/${contractId}`);
      toast.success("Contrat supprimé avec succès");
      fetchContracts();
    } catch (error) {
      console.error("Error deleting contract:", error);
      toast.error("Erreur lors de la suppression du contrat");
    }
  };

  const handleViewDetails = (contract) => {
    setSelectedContract(contract);
    setShowDetailsDialog(true);
  };

  const handleRenewContract = (contract) => {
    // Mock renewal process
    toast.success(`Processus de renouvellement lancé pour ${getClientName(contract.client_id)}`);
    // In real implementation, this would open a renewal wizard or redirect to renewal form
  };

  // Gestion des décommissions
  const handleOpenDecommissionsDialog = async (contract) => {
    setSelectedContract(contract);
    setLoadingDecommissions(true);
    setShowDecommissionsDialog(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/contracts/${contract.id}/decommissions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDecommissions(response.data || []);
    } catch (error) {
      console.error("Error fetching decommissions:", error);
      toast.error("Erreur lors du chargement des décommissions");
      setDecommissions([]);
    } finally {
      setLoadingDecommissions(false);
    }
  };

  const handleAddDecommission = async () => {
    if (!decommissionForm.montant || parseFloat(decommissionForm.montant) <= 0) {
      toast.error("Veuillez saisir un montant valide");
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/contracts/${selectedContract.id}/decommissions`,
        {
          ...decommissionForm,
          montant: parseFloat(decommissionForm.montant),
          date_decommission: new Date(decommissionForm.date_decommission).toISOString()
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success("Décommission ajoutée avec succès");
      
      // Réinitialiser le formulaire
      setDecommissionForm({
        type: 'partielle',
        montant: '',
        date_decommission: new Date().toISOString().split('T')[0],
        raison: ''
      });
      
      // Recharger les décommissions
      handleOpenDecommissionsDialog(selectedContract);
    } catch (error) {
      console.error("Error adding decommission:", error);
      toast.error("Erreur lors de l'ajout de la décommission");
    }
  };

  const handleDeleteDecommission = async (decommissionId) => {
    if (!confirm("Êtes-vous sûr de vouloir supprimer cette décommission ?")) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/decommissions/${decommissionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Décommission supprimée avec succès");
      
      // Recharger les décommissions
      handleOpenDecommissionsDialog(selectedContract);
    } catch (error) {
      console.error("Error deleting decommission:", error);
      toast.error("Erreur lors de la suppression de la décommission");
    }
  };

  const handleOpenTreasuryModal = async (contract) => {
    setSelectedContract(contract);
    
    try {
      // Récupérer les données de trésorerie existantes pour ce contrat
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/contracts/${contract.id}/treasury`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setContractTreasuryData(response.data || {
        montant_avance: contract.montant_avance || 0,
        paiement_mensuel: contract.paiement_mensuel || 0,
        avances_supplementaires: contract.avances_supplementaires || []
      });
    } catch (error) {
      console.error("Error fetching contract treasury data:", error);
      // Utiliser les données du contrat ou valeurs par défaut
      setContractTreasuryData({
        montant_avance: contract.montant_avance || 0,
        paiement_mensuel: contract.paiement_mensuel || 0,
        avances_supplementaires: contract.avances_supplementaires || []
      });
    }
    
    setShowTreasuryModal(true);
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(price);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  const filteredContracts = contracts.filter(contract => {
    const clientName = getClientName(contract.client_id);
    
    // Filtre recherche texte
    const matchesSearch = clientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
           contract.supplier_name.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Filtre assignation
    const matchesAssignation = filterAssignedTo === 'all' || contract.assigned_commercial === filterAssignedTo;
    
    // Filtre statut
    const matchesStatus = filterStatus === 'all' || contract.status === filterStatus;
    
    // Filtre date début
    const matchesStartDate = !filterStartDate || (contract.start_date && new Date(contract.start_date) >= filterStartDate);
    
    // Filtre date fin
    const matchesEndDate = !filterEndDate || (contract.end_date && new Date(contract.end_date) <= filterEndDate);
    
    return matchesSearch && matchesAssignation && matchesStatus && matchesStartDate && matchesEndDate;
  });

  // Helper pour déterminer si un contrat est actif basé sur les dates
  const isContractActive = (contract) => {
    if (!contract.start_date || !contract.end_date) return false;
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const startDate = new Date(contract.start_date);
    startDate.setHours(0, 0, 0, 0);
    
    const endDate = new Date(contract.end_date);
    endDate.setHours(23, 59, 59, 999);
    
    return startDate <= today && today <= endDate;
  };

  const activeContracts = contracts.filter(c => isContractActive(c));
  const expiringContracts = activeContracts.filter(c => {
    const days = getDaysUntilExpiry(c.end_date);
    return days >= 0 && days <= 30; // Expire dans 0 à 30 jours (exclu les contrats déjà expirés)
  });
  
  // CA ANNUEL = Somme des TOTAL CONTRAT de TOUS les contrats (tous statuts confondus)
  const totalAnnualValue = contracts.reduce((sum, c) => sum + calculateTotalContractValue(c), 0);
  
  // COMMISSIONS = Somme des commissions des contrats ACTIFS uniquement
  const totalCommission = activeContracts.reduce((sum, c) => {
    const contractTotal = calculateTotalContractValue(c);
    return sum + (contractTotal * (c.commission_rate || 0));
  }, 0);

  if (loading) {
    return (
      <div className="p-8">
        <div className="space-y-6">
          {[...Array(3)].map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-6">
                <div className="h-6 bg-gray-200 rounded mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-2/3"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Gestion des Contrats</h1>
          <p className="text-gray-600 mt-1">Suivez les échéances et gérez votre portefeuille contractuel</p>
        </div>
        <div className="flex items-center space-x-2 text-blue-600">
          <FileText className="w-6 h-6" />
          <span className="font-medium">Suivi automatisé</span>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        <Card className="hover-lift">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Contrats Actifs</p>
                <p className="text-3xl font-bold text-gray-900">{activeContracts.length}</p>
              </div>
              <div className="p-3 bg-emerald-100 rounded-lg">
                <FileText className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="hover-lift">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Échéances 30j</p>
                <p className="text-3xl font-bold text-amber-600">{expiringContracts.length}</p>
              </div>
              <div className="p-3 bg-amber-100 rounded-lg">
                <AlertTriangle className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="hover-lift">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">CA Annuel</p>
                <p className="text-2xl font-bold text-blue-600">{formatPrice(totalAnnualValue)}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-lg">
                <Euro className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="hover-lift">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Commissions</p>
                <p className="text-2xl font-bold text-green-600">{formatPrice(totalCommission)}</p>
              </div>
              <div className="p-3 bg-green-100 rounded-lg">
                <TrendingUp className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="flex items-center space-x-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <Input
            placeholder="Rechercher un contrat..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="search-contracts"
          />
        </div>
        <div className="text-sm text-gray-500">
          {filteredContracts.length} contrat(s) trouvé(s)
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowContractFilters(!showContractFilters)}
        >
          {showContractFilters ? 'Masquer' : 'Filtres Avancés'}
        </Button>
      </div>

      {/* Filtres Avancés */}
      {showContractFilters && (
        <Card className="border-blue-200 bg-blue-50/50">
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Filtre Assignation */}
              <div>
                <Label className="text-sm font-medium mb-2 block">Commercial Assigné</Label>
                <Select value={filterAssignedTo} onValueChange={setFilterAssignedTo}>
                  <SelectTrigger>
                    <SelectValue placeholder="Tous" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous</SelectItem>
                    {commercials.map(c => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.first_name} {c.last_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Filtre Statut */}
              <div>
                <Label className="text-sm font-medium mb-2 block">Statut</Label>
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger>
                    <SelectValue placeholder="Tous" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous</SelectItem>
                    <SelectItem value="active">Actif</SelectItem>
                    <SelectItem value="expired">Expiré</SelectItem>
                    <SelectItem value="pending">En attente</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Filtre Date Début */}
              <div>
                <Label className="text-sm font-medium mb-2 block">Date de début après</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left font-normal">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {filterStartDate ? format(filterStartDate, 'dd/MM/yyyy', { locale: fr }) : "Sélectionner"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <CalendarComponent
                      mode="single"
                      selected={filterStartDate}
                      onSelect={setFilterStartDate}
                      locale={fr}
                    />
                  </PopoverContent>
                </Popover>
              </div>

              {/* Filtre Date Fin */}
              <div>
                <Label className="text-sm font-medium mb-2 block">Date de fin avant</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left font-normal">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {filterEndDate ? format(filterEndDate, 'dd/MM/yyyy', { locale: fr }) : "Sélectionner"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <CalendarComponent
                      mode="single"
                      selected={filterEndDate}
                      onSelect={setFilterEndDate}
                      locale={fr}
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            {/* Bouton Réinitialiser */}
            <div className="mt-4 flex justify-end">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setFilterAssignedTo('all');
                  setFilterStatus('all');
                  setFilterStartDate(null);
                  setFilterEndDate(null);
                }}
              >
                Réinitialiser les filtres
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Contracts List */}
      <div className="space-y-4">
        {filteredContracts.map((contract) => (
          <Card key={contract.id} className="hover-lift" data-testid={`contract-card-${contract.id}`}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-4">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    {contract.contract_type === 'electricity' ? (
                      <Zap className="w-5 h-5 text-blue-600" />
                    ) : (
                      <Building2 className="w-5 h-5 text-blue-600" />
                    )}
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {getClientName(contract.client_id)} - {contract.supplier_name}
                    </h3>
                    <p className="text-sm text-gray-600">
                      Contrat {contract.contract_type === 'electricity' ? 'électricité' : 'gaz'}
                    </p>
                    {/* PDL en évidence */}
                    <div className="flex items-center gap-2 mt-1">
                      <Badge variant="outline" className="text-xs bg-yellow-50 text-yellow-700 border-yellow-300">
                        PDL: {contract.pdl || 'Non renseigné'}
                      </Badge>
                      {contract.created_by_name && (
                        <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-300">
                          <User className="w-3 h-3 mr-1" />
                          Créé par: {contract.created_by_name}
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  {getExpiryBadge(contract.end_date)}
                  <Badge className={getStatusColor(contract.status)}>
                    {getStatusLabel(contract.status)}
                  </Badge>
                </div>
              </div>

              {/* Adresse de consommation en évidence */}
              <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center mb-1">
                  <MapPin className="w-4 h-4 text-gray-500 mr-2" />
                  <span className="text-sm font-medium text-gray-700">Adresse de consommation :</span>
                </div>
                <p className="text-sm text-gray-900 ml-6">
                  {contract.adresse_site || 'Non renseignée'}
                  {contract.code_postal_site && contract.ville_site && (
                    <span>, {contract.code_postal_site} {contract.ville_site}</span>
                  )}
                </p>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Début:</span>
                  <div className="font-medium">{formatDate(contract.start_date)}</div>
                </div>
                <div>
                  <span className="text-gray-500">Fin:</span>
                  <div className="font-medium">{formatDate(contract.end_date)}</div>
                </div>
                <div>
                  <span className="text-gray-500">Puissance:</span>
                  <div className="font-medium">{contract.power_subscribed} kVA</div>
                </div>
                <div>
                  <span className="text-gray-500">Consommation:</span>
                  <div className="font-medium">{contract.annual_consumption.toLocaleString()} kWh</div>
                </div>
                <div>
                  <span className="text-gray-500">Coût annuel (base):</span>
                  <div className="font-medium text-blue-600">{formatPrice(contract.annual_cost)}</div>
                </div>
                {calculateTotalAvances(contract) > 0 && (
                  <div>
                    <span className="text-gray-500">Avances totales:</span>
                    <div className="font-medium text-orange-600">{formatPrice(calculateTotalAvances(contract))}</div>
                  </div>
                )}
                <div>
                  <span className="text-gray-500">Total contrat:</span>
                  <div className="font-bold text-green-600 text-lg">{formatPrice(calculateTotalContractValue(contract))}</div>
                  {calculateTotalContractValue(contract) !== contract.annual_cost && (
                    <div className="text-xs text-gray-500">
                      Incl. {calculateTotalAvances(contract) > 0 ? 'avances + ' : ''}paiements
                    </div>
                  )}
                </div>
              </div>

              {/* Section Trésorerie */}
              {(contract.montant_avance > 0 || contract.paiement_mensuel > 0) && (
                <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center mb-2">
                    <Euro className="w-4 h-4 text-blue-600 mr-2" />
                    <span className="text-sm font-medium text-blue-900">Configuration Trésorerie</span>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    {contract.montant_avance > 0 && (
                      <div>
                        <span className="text-blue-600">Avance:</span>
                        <div className="font-medium">{formatPrice(contract.montant_avance)}</div>
                      </div>
                    )}
                    {contract.montant_deuxieme_tranche > 0 && (
                      <div>
                        <span className="text-orange-600">2ème tranche:</span>
                        <div className="font-medium">{formatPrice(contract.montant_deuxieme_tranche)}</div>
                      </div>
                    )}
                    {contract.paiement_mensuel > 0 && (
                      <div>
                        <span className="text-green-600">Mensuel:</span>
                        <div className="font-medium">{formatPrice(contract.paiement_mensuel)} × {contract.duree_mois || 12}</div>
                      </div>
                    )}
                    {(contract.montant_avance > 0 || contract.paiement_mensuel > 0) && (
                      <div>
                        <span className="text-purple-600">Total:</span>
                        <div className="font-bold">
                          {formatPrice(
                            (contract.montant_avance || 0) + 
                            (contract.montant_deuxieme_tranche || 0) +
                            ((contract.paiement_mensuel || 0) * (contract.duree_mois || 12))
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="mt-4 flex items-center justify-between">
                <div className="flex items-center space-x-2 text-sm text-gray-600">
                  <span>Prix: {contract.unit_price.toFixed(3)} €/kWh</span>
                  <span>•</span>
                  <span>Commission: {(contract.commission_rate * 100).toFixed(1)}%</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="text-sm font-medium text-green-600">
                    Commission: {new Intl.NumberFormat('fr-FR', {
                      style: 'currency',
                      currency: 'EUR'
                    }).format(contract.annual_cost * contract.commission_rate)}
                  </div>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    className="text-blue-600 border-blue-300 hover:bg-blue-50"
                    onClick={() => handleOpenTreasuryModal(contract)}
                  >
                    <Euro className="w-4 h-4 mr-1" />
                    Gérer Trésorerie
                  </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => handleViewDetails(contract)}
                      data-testid={`view-contract-${contract.id}`}
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      Voir détails
                    </Button>
                    {getDaysUntilExpiry(contract.end_date) <= 30 && (
                      <Button 
                        size="sm" 
                        className="bg-amber-600 hover:bg-amber-700" 
                        onClick={() => handleRenewContract(contract)}
                        data-testid={`renew-contract-${contract.id}`}
                      >
                        <RefreshCw className="w-4 h-4 mr-1" />
                        Renouveler
                      </Button>
                    )}
                  </div>
                </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredContracts.length === 0 && (
        <div className="text-center py-12">
          <FileText className="w-16 h-16 mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun contrat trouvé</h3>
          <p className="text-gray-500 mb-6">
            {searchTerm ? "Aucun contrat ne correspond à votre recherche" : "Aucun contrat disponible"}
          </p>
        </div>
      )}
      
      {/* Contract Details Dialog */}
      <Dialog open={showDetailsDialog} onOpenChange={setShowDetailsDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Détails du Contrat</DialogTitle>
            <DialogDescription>
              Informations complètes du contrat sélectionné
            </DialogDescription>
          </DialogHeader>
          
          {selectedContract && (
            <div className="space-y-6">
              {/* Contract Header */}
              <div className="flex items-center justify-between p-6 bg-gray-50 rounded-lg">
                <div>
                  <h3 className="text-xl font-semibold">{getClientName(selectedContract.client_id)}</h3>
                  <p className="text-gray-600">{selectedContract.supplier_name} - {selectedContract.contract_type === 'electricity' ? 'Électricité' : 'Gaz'}</p>
                </div>
                <div className="text-right">
                  <Badge className={getStatusColor(selectedContract.status)}>
                    {getStatusLabel(selectedContract.status)}
                  </Badge>
                  {getExpiryBadge(selectedContract.end_date)}
                </div>
              </div>

              {/* Contract Details Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Informations du Contrat</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div>
                      <span className="text-sm text-gray-500">Date de début:</span>
                      <div className="font-medium">{formatDate(selectedContract.start_date)}</div>
                    </div>
                    <div>
                      <span className="text-sm text-gray-500">Date de fin:</span>
                      <div className="font-medium">{formatDate(selectedContract.end_date)}</div>
                    </div>
                    <div>
                      <span className="text-sm text-gray-500">Puissance souscrite:</span>
                      <div className="font-medium">{selectedContract.power_subscribed} kVA</div>
                    </div>
                    <div>
                      <span className="text-sm text-gray-500">Consommation annuelle:</span>
                      <div className="font-medium">{selectedContract.annual_consumption.toLocaleString()} kWh</div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Informations Financières</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div>
                      <span className="text-sm text-gray-500">Prix unitaire:</span>
                      <div className="font-medium">{selectedContract.unit_price.toFixed(3)} €/kWh</div>
                    </div>
                    <div>
                      <span className="text-sm text-gray-500">Coût annuel (base):</span>
                      <div className="font-medium text-blue-600">{formatPrice(selectedContract.annual_cost)}</div>
                    </div>
                    {calculateTotalAvances(selectedContract) > 0 && (
                      <div>
                        <span className="text-sm text-gray-500">Total avances:</span>
                        <div className="font-medium text-orange-600">
                          {formatPrice(calculateTotalAvances(selectedContract))}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          {selectedContract.montant_avance > 0 && `Initiale: ${formatPrice(selectedContract.montant_avance)}`}
                          {selectedContract.avances_supplementaires?.length > 0 && 
                            ` + ${selectedContract.avances_supplementaires.length} supplémentaire(s)`}
                        </div>
                      </div>
                    )}
                    <div className="pt-2 border-t">
                      <span className="text-sm text-gray-500">Total contrat:</span>
                      <div className="font-bold text-green-600 text-xl">
                        {formatPrice(calculateTotalContractValue(selectedContract))}
                      </div>
                    </div>
                    <div>
                      <span className="text-sm text-gray-500">Taux de commission:</span>
                      <div className="font-medium">{(selectedContract.commission_rate * 100).toFixed(1)}%</div>
                    </div>
                    <div>
                      <span className="text-sm text-gray-500">Commission totale:</span>
                      <div className="font-medium text-green-600">{formatPrice(selectedContract.annual_cost * selectedContract.commission_rate)}</div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Actions */}
              <div className="flex justify-end space-x-4 pt-4 border-t">
                <Button variant="outline" onClick={() => setShowDetailsDialog(false)}>
                  Fermer
                </Button>
                <Button 
                  variant="outline"
                  className="text-violet-600 hover:text-violet-700 hover:bg-violet-50"
                  onClick={() => handleOpenDecommissionsDialog(selectedContract)}
                >
                  Gérer Décommissions
                </Button>
                <Button 
                  variant="outline"
                  className="text-red-600 hover:text-red-700"
                  onClick={() => handleDeleteContract(selectedContract.id)}
                >
                  Supprimer le contrat
                </Button>
                <Button 
                  className="bg-blue-600 hover:bg-blue-700"
                  onClick={() => handleEditContract(selectedContract)}
                >
                  Modifier le contrat
                </Button>
                {getDaysUntilExpiry(selectedContract.end_date) <= 30 && (
                  <Button className="bg-amber-600 hover:bg-amber-700" onClick={() => handleRenewContract(selectedContract)}>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Lancer le renouvellement
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Contract Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Modifier le Contrat</DialogTitle>
            <DialogDescription>
              Modifiez les informations du contrat
            </DialogDescription>
          </DialogHeader>
          
          {selectedContract && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="supplier_name">Fournisseur</Label>
                  <Input
                    id="supplier_name"
                    value={editFormData.supplier_name || ''}
                    onChange={(e) => setEditFormData({...editFormData, supplier_name: e.target.value})}
                  />
                </div>
                <div>
                  <Label htmlFor="contract_type">Type de contrat</Label>
                  <Select value={editFormData.contract_type} onValueChange={(value) => setEditFormData({...editFormData, contract_type: value})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="electricity">Électricité</SelectItem>
                      <SelectItem value="gas">Gaz</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Informations site/livraison - Section importante */}
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h3 className="text-sm font-semibold text-blue-900 mb-3">📍 Informations du Site de Consommation</h3>
                
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <Label className="font-medium text-blue-800">PDL (Point De Livraison) *</Label>
                    <Input
                      value={editFormData.pdl || ''}
                      onChange={(e) => setEditFormData({...editFormData, pdl: e.target.value})}
                      placeholder="ex: 09234567890123"
                      className="bg-white"
                    />
                    <p className="text-xs text-blue-600 mt-1">Identifiant unique du compteur (obligatoire)</p>
                  </div>
                  
                  <div>
                    <Label className="font-medium text-blue-800">Adresse du site de consommation *</Label>
                    <Input
                      value={editFormData.adresse_site || ''}
                      onChange={(e) => setEditFormData({...editFormData, adresse_site: e.target.value})}
                      placeholder="Adresse complète du site"
                      className="bg-white"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="font-medium text-blue-800">Code postal</Label>
                      <Input
                        value={editFormData.code_postal_site || ''}
                        onChange={(e) => setEditFormData({...editFormData, code_postal_site: e.target.value})}
                        placeholder="69000"
                        className="bg-white"
                      />
                    </div>
                    <div>
                      <Label className="font-medium text-blue-800">Ville</Label>
                      <Input
                        value={editFormData.ville_site || ''}
                        onChange={(e) => setEditFormData({...editFormData, ville_site: e.target.value})}
                        placeholder="Lyon"
                        className="bg-white"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Dates du contrat */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="start_date">Date de début</Label>
                  <Input
                    id="start_date"
                    type="date"
                    value={editFormData.start_date || ''}
                    onChange={(e) => setEditFormData({...editFormData, start_date: e.target.value})}
                  />
                </div>
                <div>
                  <Label htmlFor="end_date">Date de fin</Label>
                  <Input
                    id="end_date"
                    type="date"
                    value={editFormData.end_date || ''}
                    onChange={(e) => setEditFormData({...editFormData, end_date: e.target.value})}
                  />
                </div>
              </div>

              {/* Informations techniques et financières */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="power_subscribed">Puissance souscrite (kVA)</Label>
                  <Input
                    id="power_subscribed"
                    type="number"
                    step="0.1"
                    value={editFormData.power_subscribed || ''}
                    onChange={(e) => setEditFormData({...editFormData, power_subscribed: parseFloat(e.target.value)})}
                    placeholder="36"
                  />
                </div>
                <div>
                  <Label htmlFor="annual_consumption">Consommation annuelle (kWh)</Label>
                  <Input
                    id="annual_consumption"
                    type="number"
                    value={editFormData.annual_consumption || ''}
                    onChange={(e) => setEditFormData({...editFormData, annual_consumption: parseFloat(e.target.value)})}
                    placeholder="25000"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="unit_price">Prix unitaire (€/kWh)</Label>
                  <Input
                    id="unit_price"
                    type="number"
                    step="0.001"
                    value={editFormData.unit_price || ''}
                    onChange={(e) => setEditFormData({...editFormData, unit_price: parseFloat(e.target.value)})}
                    placeholder="0.156"
                  />
                </div>
                <div>
                  <Label htmlFor="annual_cost">Coût annuel (€)</Label>
                  <Input
                    id="annual_cost"
                    type="number"
                    step="0.01"
                    value={editFormData.annual_cost || ''}
                    onChange={(e) => setEditFormData({...editFormData, annual_cost: parseFloat(e.target.value)})}
                    placeholder="3900"
                  />
                </div>
                <div>
                  <Label htmlFor="commission_rate">Taux de commission (%)</Label>
                  <Input
                    id="commission_rate"
                    type="number"
                    step="0.1"
                    value={editFormData.commission_rate ? (editFormData.commission_rate * 100) : ''}
                    onChange={(e) => setEditFormData({...editFormData, commission_rate: parseFloat(e.target.value) / 100})}
                    placeholder="5"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="status">Statut</Label>
                <Select value={editFormData.status} onValueChange={(value) => setEditFormData({...editFormData, status: value})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Actif</SelectItem>
                    <SelectItem value="pending">En attente</SelectItem>
                    <SelectItem value="expired">Expiré</SelectItem>
                    <SelectItem value="cancelled">Annulé</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex justify-end space-x-2 pt-4">
                <Button variant="outline" onClick={() => setShowEditDialog(false)}>
                  Annuler
                </Button>
                <Button onClick={handleSaveContract}>
                  Enregistrer les modifications
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Decommissions Dialog */}
      <Dialog open={showDecommissionsDialog} onOpenChange={setShowDecommissionsDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-violet-700">Gestion des Décommissions</DialogTitle>
            <DialogDescription>
              {selectedContract && `Contrat: ${getClientName(selectedContract.client_id)} - ${selectedContract.supplier_name}`}
            </DialogDescription>
          </DialogHeader>

          {selectedContract && (
            <div className="space-y-6">
              {/* Formulaire d'ajout */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Ajouter une Décommission</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="dec-type">Type de décommission *</Label>
                      <Select
                        value={decommissionForm.type}
                        onValueChange={(value) => {
                          let newMontant = decommissionForm.montant;
                          
                          // Si on passe à "totale", calculer automatiquement le montant
                          if (value === 'totale' && selectedContract) {
                            let totalContractAmount = 0;
                            
                            // Ajouter le montant de l'avance initiale
                            if (selectedContract.montant_avance) {
                              totalContractAmount += parseFloat(selectedContract.montant_avance);
                            }
                            
                            // Ajouter les avances supplémentaires
                            if (selectedContract.avances_supplementaires && selectedContract.avances_supplementaires.length > 0) {
                              selectedContract.avances_supplementaires.forEach(avance => {
                                totalContractAmount += parseFloat(avance.montant || 0);
                              });
                            }
                            
                            // Ajouter les paiements mensuels si mode "monthly"
                            if (selectedContract.payment_mode === 'monthly' && selectedContract.paiement_mensuel) {
                              // Calculer le nombre de mois restants
                              const startDate = new Date(selectedContract.start_date);
                              const endDate = new Date(selectedContract.end_date);
                              const monthsDiff = Math.max(0, (endDate.getFullYear() - startDate.getFullYear()) * 12 + endDate.getMonth() - startDate.getMonth());
                              totalContractAmount += parseFloat(selectedContract.paiement_mensuel) * monthsDiff;
                            }
                            
                            newMontant = totalContractAmount.toFixed(2);
                          }
                          
                          setDecommissionForm({...decommissionForm, type: value, montant: newMontant});
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="partielle">Partielle</SelectItem>
                          <SelectItem value="totale">Totale</SelectItem>
                        </SelectContent>
                      </Select>
                      {decommissionForm.type === 'totale' && (
                        <p className="text-xs text-red-600 mt-1">
                          ⚠️ Supprimera toutes les avances futures
                        </p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="dec-montant">Montant (€) *</Label>
                      <Input
                        id="dec-montant"
                        type="number"
                        step="0.01"
                        value={decommissionForm.montant}
                        onChange={(e) => setDecommissionForm({...decommissionForm, montant: e.target.value})}
                        placeholder="Ex: 1500.00"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="dec-date">Date de la décommission *</Label>
                    <Input
                      id="dec-date"
                      type="date"
                      value={decommissionForm.date_decommission}
                      onChange={(e) => setDecommissionForm({...decommissionForm, date_decommission: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label htmlFor="dec-raison">Raison</Label>
                    <Input
                      id="dec-raison"
                      value={decommissionForm.raison}
                      onChange={(e) => setDecommissionForm({...decommissionForm, raison: e.target.value})}
                      placeholder="Ex: Résiliation anticipée du contrat"
                    />
                  </div>
                  <Button 
                    onClick={handleAddDecommission}
                    className="w-full bg-violet-600 hover:bg-violet-700"
                  >
                    Ajouter la Décommission
                  </Button>
                </CardContent>
              </Card>

              {/* Liste des décommissions */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Décommissions Existantes</CardTitle>
                </CardHeader>
                <CardContent>
                  {loadingDecommissions ? (
                    <div className="text-center py-8 text-gray-500">Chargement...</div>
                  ) : decommissions.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      Aucune décommission pour ce contrat
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {decommissions.map((dec) => (
                        <div 
                          key={dec.id}
                          className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50"
                        >
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <Badge className={dec.type === 'totale' ? 'bg-red-600' : 'bg-orange-600'}>
                                {dec.type === 'totale' ? 'TOTALE' : 'PARTIELLE'}
                              </Badge>
                              <span className="font-semibold text-violet-700">
                                {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(dec.montant)}
                              </span>
                            </div>
                            <div className="text-sm text-gray-600">
                              Date: {new Date(dec.date_decommission).toLocaleDateString('fr-FR')}
                            </div>
                            {dec.raison && (
                              <div className="text-sm text-gray-500 mt-1">
                                Raison: {dec.raison}
                              </div>
                            )}
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => handleDeleteDecommission(dec.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              <div className="flex justify-end">
                <Button variant="outline" onClick={() => setShowDecommissionsDialog(false)}>
                  Fermer
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Treasury Management Modal */}
      <Dialog open={showTreasuryModal} onOpenChange={setShowTreasuryModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Gestion Trésorerie - {selectedContract && getClientName(selectedContract.client_id)}
            </DialogTitle>
            <div className="text-sm text-gray-600">
              {selectedContract && (
                <>
                  Contrat {selectedContract.supplier_name} • PDL: {selectedContract.pdl} • 
                  Commission totale: {new Intl.NumberFormat('fr-FR', {
                    style: 'currency',
                    currency: 'EUR'
                  }).format(selectedContract.annual_cost * selectedContract.commission_rate)}
                </>
              )}
            </div>
          </DialogHeader>
          
          {selectedContract && (
            <div className="space-y-6">
              {/* Paiement d'avance initial */}
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h3 className="text-sm font-semibold text-blue-900 mb-3">💰 Paiement d'Avance Initial</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="font-medium text-blue-800">Montant de l'avance (€)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={contractTreasuryData.montant_avance}
                      onChange={(e) => setContractTreasuryData({
                        ...contractTreasuryData,
                        montant_avance: parseFloat(e.target.value) || 0
                      })}
                      placeholder="0.00"
                      className="bg-white"
                    />
                    <p className="text-xs text-blue-600 mt-1">Paiement initial à l'avance</p>
                  </div>
                  
                  <div>
                    <Label className="font-medium text-blue-800">Date de règlement</Label>
                    <Input
                      type="date"
                      value={contractTreasuryData.date_reglement_avance || ''}
                      onChange={(e) => setContractTreasuryData({
                        ...contractTreasuryData,
                        date_reglement_avance: e.target.value
                      })}
                      className="bg-white"
                    />
                    <p className="text-xs text-blue-600 mt-1">Date de réception du paiement</p>
                  </div>
                  
                  <div>
                    <Label className="font-medium text-blue-800">Status Avance</Label>
                    <select 
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-sm"
                      value={contractTreasuryData.avance_status || 'pending'}
                      onChange={(e) => setContractTreasuryData({
                        ...contractTreasuryData,
                        avance_status: e.target.value
                      })}
                    >
                      <option value="pending">En attente</option>
                      <option value="received">Reçu</option>
                      <option value="partial">Partiel</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Paiements mensuels */}
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <h3 className="text-sm font-semibold text-green-900 mb-3">📅 Paiements Mensuels Récurrents</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="font-medium text-green-800">Montant mensuel (€)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={contractTreasuryData.paiement_mensuel}
                      onChange={(e) => setContractTreasuryData({
                        ...contractTreasuryData,
                        paiement_mensuel: parseFloat(e.target.value) || 0
                      })}
                      placeholder="0.00"
                      className="bg-white"
                    />
                  </div>
                  
                  <div>
                    <Label className="font-medium text-green-800">Durée (mois)</Label>
                    <Input
                      type="number"
                      value={contractTreasuryData.duree_mois || 12}
                      onChange={(e) => setContractTreasuryData({
                        ...contractTreasuryData,
                        duree_mois: parseInt(e.target.value) || 12
                      })}
                      placeholder="12"
                      className="bg-white"
                    />
                  </div>
                  
                  <div>
                    <Label className="font-medium text-green-800">Date début</Label>
                    <Input
                      type="date"
                      value={contractTreasuryData.date_debut_mensuel || ''}
                      onChange={(e) => setContractTreasuryData({
                        ...contractTreasuryData,
                        date_debut_mensuel: e.target.value
                      })}
                      className="bg-white"
                    />
                  </div>
                </div>
                
                <div className="mt-3 text-sm text-green-700">
                  Total mensuel sur {contractTreasuryData.duree_mois || 12} mois: {" "}
                  <span className="font-semibold">
                    {new Intl.NumberFormat('fr-FR', {
                      style: 'currency',
                      currency: 'EUR'
                    }).format((contractTreasuryData.paiement_mensuel || 0) * (contractTreasuryData.duree_mois || 12))}
                  </span>
                </div>
              </div>

              {/* Avances supplémentaires */}
              <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
                <div className="flex justify-between items-center mb-3">
                  <h3 className="text-sm font-semibold text-orange-900">🔄 Avances Supplémentaires</h3>
                  <Button 
                    size="sm" 
                    className="bg-orange-600 hover:bg-orange-700"
                    onClick={() => {
                      const newAvance = {
                        id: Date.now(),
                        montant: 0,
                        date: new Date().toISOString().split('T')[0],
                        description: '',
                        status: 'pending'
                      };
                      setContractTreasuryData({
                        ...contractTreasuryData,
                        avances_supplementaires: [...(contractTreasuryData.avances_supplementaires || []), newAvance]
                      });
                    }}
                  >
                    + Ajouter Avance
                  </Button>
                </div>
                
                {contractTreasuryData.avances_supplementaires?.map((avance, index) => (
                  <div key={avance.id || index} className="grid grid-cols-4 gap-3 mb-3 p-3 bg-white border border-orange-200 rounded">
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="Montant €"
                      value={avance.montant}
                      onChange={(e) => {
                        const updated = [...contractTreasuryData.avances_supplementaires];
                        updated[index].montant = parseFloat(e.target.value) || 0;
                        setContractTreasuryData({...contractTreasuryData, avances_supplementaires: updated});
                      }}
                    />
                    <Input
                      type="date"
                      value={avance.date}
                      onChange={(e) => {
                        const updated = [...contractTreasuryData.avances_supplementaires];
                        updated[index].date = e.target.value;
                        setContractTreasuryData({...contractTreasuryData, avances_supplementaires: updated});
                      }}
                    />
                    <Input
                      placeholder="Description"
                      value={avance.description}
                      onChange={(e) => {
                        const updated = [...contractTreasuryData.avances_supplementaires];
                        updated[index].description = e.target.value;
                        setContractTreasuryData({...contractTreasuryData, avances_supplementaires: updated});
                      }}
                    />
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => {
                        const updated = contractTreasuryData.avances_supplementaires.filter((_, i) => i !== index);
                        setContractTreasuryData({...contractTreasuryData, avances_supplementaires: updated});
                      }}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                
                {(!contractTreasuryData.avances_supplementaires || contractTreasuryData.avances_supplementaires.length === 0) && (
                  <p className="text-center text-orange-600 text-sm py-4">
                    Aucune avance supplémentaire configurée
                  </p>
                )}
              </div>

              {/* Résumé total */}
              <div className="p-4 bg-gray-100 border border-gray-300 rounded-lg">
                <h3 className="text-sm font-semibold text-gray-900 mb-3">📊 Résumé Financier</h3>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Avance initiale:</span>
                    <div className="font-semibold text-blue-600">
                      {new Intl.NumberFormat('fr-FR', {
                        style: 'currency',
                        currency: 'EUR'
                      }).format(contractTreasuryData.montant_avance || 0)}
                    </div>
                  </div>
                  <div>
                    <span className="text-gray-600">Total mensuel:</span>
                    <div className="font-semibold text-green-600">
                      {new Intl.NumberFormat('fr-FR', {
                        style: 'currency',
                        currency: 'EUR'
                      }).format((contractTreasuryData.paiement_mensuel || 0) * (contractTreasuryData.duree_mois || 12))}
                    </div>
                  </div>
                  <div>
                    <span className="text-gray-600">Avances supplémentaires:</span>
                    <div className="font-semibold text-orange-600">
                      {new Intl.NumberFormat('fr-FR', {
                        style: 'currency',
                        currency: 'EUR'
                      }).format((contractTreasuryData.avances_supplementaires || []).reduce((sum, avance) => sum + (avance.montant || 0), 0))}
                    </div>
                  </div>
                </div>
                
                <div className="mt-3 pt-3 border-t border-gray-300">
                  <div className="flex justify-between items-center">
                    <span className="font-semibold">TOTAL ENCAISSEMENT PRÉVU:</span>
                    <span className="font-bold text-lg text-purple-600">
                      {new Intl.NumberFormat('fr-FR', {
                        style: 'currency',
                        currency: 'EUR'
                      }).format(
                        (contractTreasuryData.montant_avance || 0) + 
                        ((contractTreasuryData.paiement_mensuel || 0) * (contractTreasuryData.duree_mois || 12)) + 
                        ((contractTreasuryData.avances_supplementaires || []).reduce((sum, avance) => sum + (avance.montant || 0), 0))
                      )}
                    </span>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end space-x-2 pt-4 border-t">
                <Button 
                  variant="outline" 
                  onClick={() => setShowTreasuryModal(false)}
                >
                  Annuler
                </Button>
                <Button 
                  className="bg-blue-600 hover:bg-blue-700"
                  onClick={async () => {
                    try {
                      const token = localStorage.getItem('token');
                      
                      // Sauvegarder la configuration
                      await axios.put(`${API}/contracts/${selectedContract.id}/treasury`, contractTreasuryData, {
                        headers: { Authorization: `Bearer ${token}` }
                      });
                      
                      // Recharger immédiatement les données de trésorerie pour ce contrat
                      const updatedTreasuryResponse = await axios.get(`${API}/contracts/${selectedContract.id}/treasury`, {
                        headers: { Authorization: `Bearer ${token}` }
                      });
                      
                      // Mettre à jour l'état avec les nouvelles données
                      setContractTreasuryData(updatedTreasuryResponse.data || {});
                      
                      toast.success("Configuration trésorerie sauvegardée");
                      
                      // Rafraîchir la liste des contrats
                      await fetchContracts();
                      
                      // Garder le modal ouvert pour voir les données mises à jour
                      // setShowTreasuryModal(false);
                    } catch (error) {
                      console.error("Error saving treasury config:", error);
                      const errorMsg = error.response?.data?.detail || "Erreur lors de la sauvegarde";
                      toast.error(errorMsg);
                    }
                  }}
                >
                  Sauvegarder Configuration
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ContractsPage;